import { Dispatch, FC, memo, SetStateAction, useState } from 'react'
import { useMenuItems } from '../hooks/useMenuItems'
import { AppMenuState, CurrentUserState, guestMode } from '@app/storage'
import { useSnapshot } from 'valtio'
import styled from 'styled-components'
import { Icon, IconColor, IconTypes, ToolTips } from '@app/ui'
import { useTranslation } from 'react-i18next'
import { MenuItemProps, MenuItemType, MenuItem } from '../types'
import { useNavigate } from 'react-router-dom'
import { useScrollLock } from '@app/helpers/hooks'
import { useGenerateInviteLinkLazyQuery, useUserChannelsLazyQuery } from '@app/graphql'
import { AuthHelper } from '@app/helpers'
import { ROUTES } from '@app/constants'

const MenuItemRow: FC<MenuItemProps & { showChevron?: boolean; isLogout?: boolean; highlighted?: boolean }> = memo((
  { iconType, title, disable, action, showChevron = true, isLogout = false, highlighted = false }
) => {
  return (
    <StyledMenuItem $disable={disable} $isLogout={isLogout} $highlighted={highlighted} onClick={action}>
      <div className="left-content">
        <div className="icon-block">
          <Icon
            type={iconType}
            color={
              highlighted
                ? IconColor.menuHighlightIcon
                : disable
                  ? IconColor.grayDark
                  : IconColor.zinc400
            }
            width={20}
            height={20}
          />
        </div>
        <span className="item-name">{title}</span>
      </div>
      {showChevron && (
        <Icon
          type={IconTypes.angleRight}
          color={IconColor.zinc400}
          width={16}
          height={16}
        />
      )}
    </StyledMenuItem>
  )
})

const DropdownMenuItem: FC<{
  item: MenuItem
  open: boolean
  onToggle: () => void
  onChildAction: (child: MenuItem) => void
}> = memo(({ item, open, onToggle, onChildAction }) => {
  return (
    <StyledDropdownWrapper $open={open}>
      <StyledDropdownHeader $open={open} onClick={onToggle}>
        <div className="left-content">
          <div className="icon-block">
            <Icon
              type={item.iconType}
              color={item.disable ? IconColor.grayDark : IconColor.zinc400}
              width={20}
              height={20}
            />
          </div>
          <span className="item-name">{item.title}</span>
        </div>
        <Icon
          type={open ? IconTypes.angleUpIcon : IconTypes.angleDown}
          color={IconColor.zinc400}
          width={16}
          height={16}
        />
      </StyledDropdownHeader>
      {open && (
        <StyledDropdownChildren>
          {(item.children ?? []).filter((child) => !child.hide).map((child) => (
            <StyledSubMenuItem
              key={child.title}
              $disable={child.disable}
              $highlighted={child.highlighted}
              onClick={() => !child.disable && onChildAction(child)}
            >
              <div className="icon-block">
                <Icon
                  type={child.iconType}
                  color={
                    child.highlighted
                      ? IconColor.subMenuHighlight
                      : child.disable
                        ? IconColor.grayDark
                        : IconColor.zinc400
                  }
                  width={18}
                  height={18}
                />
              </div>
              <span className="item-name">{child.title}</span>
            </StyledSubMenuItem>
          ))}
        </StyledDropdownChildren>
      )}
    </StyledDropdownWrapper>
  )
})

export const MenuItemList = ({
  setOpenLanguageSelect,
  setChannelName,
  setShareLink,
  disableSub,
  setLoading,
  setOpenSocial,
  setShareLinkError,
  setOpenSupportSheet,
  setTelegramBotSelect,
  setBecomeTipster,
}: {
  setOpenLanguageSelect: Dispatch<SetStateAction<boolean>>
  setChannelName: Dispatch<SetStateAction<string>>
  disableSub: boolean
  setShareLink: Dispatch<SetStateAction<string>>
  setLoading: Dispatch<SetStateAction<boolean>>
  setOpenSocial: Dispatch<SetStateAction<boolean>>
  setShareLinkError: Dispatch<SetStateAction<boolean>>
  setOpenSupportSheet: Dispatch<SetStateAction<boolean>>
  setTelegramBotSelect: Dispatch<SetStateAction<boolean>>
  setBecomeTipster: Dispatch<SetStateAction<boolean>>
}) => {
  const { isTouring, tourStage, currentTourStage, id } = useSnapshot(CurrentUserState)
  const { t } = useTranslation()
  const userMenuItems = useMenuItems({
    setOpenLanguageSelect,
    disableSub,
    setTelegramBotSelect,
    setOpenSupportSheet,
    setBecomeTipster,
  })
  const isGuest = guestMode()
  const navigate = useNavigate()
  const { unlockScroll } = useScrollLock()
  const [ getUserChannels ] = useUserChannelsLazyQuery()
  const [ generateLink ] = useGenerateInviteLinkLazyQuery()
  const [ openDropdownTitle, setOpenDropdownTitle ] = useState<string | null>(null)

  const loadInviteLink = async () => {
    try {
      setLoading(true)
      setShareLink(null)
      setChannelName('')
      setOpenSocial(true)
      const resultChannels = await getUserChannels({
        variables: { userId: id },
      })
      const selectedChannel = resultChannels?.data?.userChannels?.[0]
      const channelId = selectedChannel?.id || null
      const resultLink = await generateLink({
        variables: { channelId },
      })
      const inviteLink = resultLink?.data?.inviteLink
      if (!inviteLink?.internalName) {
        setShareLink(null)
        setOpenSocial(false)
        setShareLinkError(true)
        return
      }

      const preparedLink = AuthHelper.prepareInviteLink(inviteLink.internalName)
      setShareLink(preparedLink)
      setChannelName(selectedChannel?.channelName ?? '')
    } catch (error) {
      setShareLink(null)
      setOpenSocial(false)
      setShareLinkError(true)
    } finally {
      setLoading(false)
    }
  }

  const resolveAction = async (menuItem: MenuItem) => {
    if (!isGuest && menuItem.disable) return

    unlockScroll()
    const rootElement = document.getElementById('root')
    rootElement?.removeAttribute('inert')

    switch (menuItem.type) {
      case MenuItemType.ACTION:
        await menuItem.action()
        break
      case MenuItemType.LOGOUT:
        await menuItem.action()
        AppMenuState.opened = false
        navigate(ROUTES.root)
        break
      case MenuItemType.LINK:
        AppMenuState.opened = false
        navigate(menuItem.route)
        break
      case MenuItemType.SHARE_SOCIAL:
        loadInviteLink()
        break
    }
  }

  return (
    <StyledMenuItemList>
      {userMenuItems.map((menuItem) => {
        if (menuItem.hide) return null

        if (menuItem.type === MenuItemType.DROPDOWN) {
          const open = openDropdownTitle === menuItem.title

          return (
            <DropdownMenuItem
              key={menuItem.title}
              item={menuItem}
              open={open}
              onToggle={() => setOpenDropdownTitle(open ? null : menuItem.title)}
              onChildAction={resolveAction}
            />
          )
        }

        const action = () => resolveAction(menuItem)

        const isShareApp = menuItem.type === MenuItemType.SHARE_SOCIAL
        const isLogout = menuItem.type === MenuItemType.LOGOUT ||
          menuItem.title === t('menu.logout')

        return (
          <div key={menuItem.title}>
            <MenuItemRow
              title={t(menuItem.title)}
              iconType={menuItem.iconType}
              disable={menuItem.disable}
              action={action}
              showChevron={false}
              isLogout={isLogout}
              highlighted={menuItem.highlighted}
            />

            {isTouring &&
                !tourStage.subscriptionMenu &&
                currentTourStage != 'userInviteMenu' &&
                (tourStage.ContentDataTip || tourStage.ContentImageTip) &&
                menuItem.title == t('common.subscription_plural') && (
              <ToolTips
                name={menuItem.title.replaceAll(' ', '_')}
                float="bottom"
                className="menu-toolTip"
              >
                <div className="content">
                  <p className="title">{t('subs.pop.menu.title1')}</p>
                  <p className="msg">{t('subs.pop.menu.title.sub1')}</p>
                </div>
              </ToolTips>
            )}
            {isTouring &&
                currentTourStage == 'userInviteMenu' &&
                menuItem.title == t('menu.channelInvite') && (
              <ToolTips
                name={menuItem.title.replaceAll(' ', '_')}
                float="top"
                className="menu-toolTip"
              >
                <div className="content">
                  <p className="title">{t('invite.pop.users')}</p>
                  <p className="msg">{t('invite.pop.users.sub')}</p>
                </div>
              </ToolTips>
            )}
          </div>
        )
      })}
    </StyledMenuItemList>
  )
}

const StyledMenuItemList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const StyledMenuItem = styled.button<{ $disable?: boolean; $isLogout?: boolean; $highlighted?: boolean }>`
  border: none;
  width: 100%;
  padding: 0 20px;
  cursor: ${({ $disable }) => ($disable ? 'default' : 'pointer')};
  display: flex;
  justify-content: space-between;
  align-items: center;
  text-decoration: none;
  background-color: transparent;

  .left-content {
    display: flex;
    align-items: center;
  }

  .icon-block {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-right: 12px;
  }

  --menu-highlight: var(--violet-300);
  --menu-highlight-icon: var(--violet-300);

  [data-theme="light"] & {
    --menu-highlight: var(--text-secondary);
    --menu-highlight-icon: #60A5FA;
  }

  .item-name {
    color: ${(props) =>
    props.$highlighted
      ? 'var(--menu-highlight)'
      : props.$isLogout
        ? 'var(--color-danger)'
        : props.$disable
          ? 'var(--color-gray-dark)'
          : 'var(--text-decimary)'};
    font-weight: ${({ $highlighted }) => ($highlighted ? '600' : 'inherit')};
  }
`

const StyledDropdownWrapper = styled.div<{ $open: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  border: none;
  position: relative;
  ${({ $open }) => $open && `
    z-index: 1;
  `}
`

const StyledDropdownHeader = styled.button<{ $open: boolean }>`
  border: ${({ $open }) => $open ? '1px solid rgba(255, 255, 255, 0.3)' : 'none'};
  border-radius: 12px;
  outline: none;
  width: 100%;
  padding: ${({ $open }) => $open ? '10px 20px' : '0 20px'};
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--bg-secondary);
  position: relative;
  z-index: 1;

  .left-content {
    display: flex;
    align-items: center;
  }

  .icon-block {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-right: 12px;
  }

  .item-name {
    color: var(--text-decimary);
  }
`

const StyledDropdownChildren = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: -42px;
  padding: 54px 20px 16px 52px;
  background: rgba(255, 255, 255, 0.04);
`

const StyledSubMenuItem = styled.button<{ $disable?: boolean; $highlighted?: boolean }>`
  border: none;
  width: 100%;
  padding: 0;
  cursor: ${({ $disable }) => ($disable ? 'default' : 'pointer')};
  display: flex;
  align-items: center;
  background-color: transparent;

  .icon-block {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-right: 10px;
  }

  --menu-highlight: var(--violet-300);
  --sub-menu-highlight: #EAB808;

  [data-theme="light"] & {
    --menu-highlight: var(--text-secondary);
    --sub-menu-highlight: #60A5FA;
  }

  .item-name {
    color: ${(props) =>
    props.$highlighted
      ? 'var(--menu-highlight)'
      : props.$disable
        ? 'var(--color-gray-dark)'
        : 'var(--text-decimary)'};
    font-weight: ${({ $highlighted }) => ($highlighted ? '600' : 'inherit')};
  }
`
